import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import {
  createProfileQueryKey,
  createExperienceCollaboratorsQueryKey,
} from '@/utils/queryKeys';
import type {
  AddCollaboratorToExperienceMutation,
  AddCollaboratorToExperienceMutationVariables,
} from './useAddCollaborator.generated';

const query = gql`
  mutation AddCollaboratorToExperience($input: AddExperiencePermissionsInput!) {
    addExperiencePermissions(input: $input)
  }
`;

export const useAddCollaborator = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();
  const experienceId = String(experience?.id);

  const { mutate, isPending, error, reset } = useMutation<
    AddCollaboratorToExperienceMutation,
    QueryError,
    AddCollaboratorToExperienceMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        AddCollaboratorToExperienceMutation,
        AddCollaboratorToExperienceMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceCollaboratorsQueryKey(experienceId),
      });
      queryClient.invalidateQueries({ queryKey: createProfileQueryKey() });
    },
  });

  const addCollaborator = (
    values: Omit<
      AddCollaboratorToExperienceMutationVariables['input'],
      'experienceId'
    >,
    options?: MutateOptions<
      AddCollaboratorToExperienceMutation,
      QueryError,
      AddCollaboratorToExperienceMutationVariables
    >
  ) => {
    return mutate({ input: { ...values, experienceId } }, options);
  };

  return {
    addCollaborator,
    isPending,
    reset,
    error,
  };
};
